import { create } from 'zustand';
import {
  brandDesignApi,
  type BrandClientSummary,
  type BrandDesignExtractionRequest,
  type BrandDesignExtractionResponse,
  type BrandDesignStatusResponse,
} from '@/services/api';

type View =
  | { kind: 'dashboard' }
  | { kind: 'extraction' }
  | { kind: 'tokens'; clientUid: string };

interface BrandDesignState {
  view: View;
  loading: boolean;
  error: string | null;

  // Client list
  clients: BrandClientSummary[];
  clientsLoading: boolean;

  // Extraction
  lastExtraction: BrandDesignExtractionResponse | null;
  pollingStatus: BrandDesignStatusResponse | null;
  polling: boolean;

  // Tokens
  tokens: Record<string, unknown> | null;

  setView: (view: View) => void;
  fetchClients: () => Promise<void>;
  submitExtraction: (data: BrandDesignExtractionRequest) => Promise<void>;
  submitAsyncExtraction: (data: BrandDesignExtractionRequest) => Promise<void>;
  pollStatus: (clientUid: string) => Promise<void>;
  fetchTokens: (clientUid: string) => Promise<void>;
  clearError: () => void;
}

export const useBrandDesignStore = create<BrandDesignState>((set) => ({
  view: { kind: 'dashboard' },
  loading: false,
  error: null,
  clients: [],
  clientsLoading: false,
  lastExtraction: null,
  pollingStatus: null,
  polling: false,
  tokens: null,

  setView: (view) => set({ view, error: null }),

  fetchClients: async () => {
    set({ clientsLoading: true, error: null });
    try {
      const clients = await brandDesignApi.listClients();
      set({ clients });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load clients' });
    } finally {
      set({ clientsLoading: false });
    }
  },

  submitExtraction: async (data) => {
    set({ loading: true, error: null, lastExtraction: null });
    try {
      const result = await brandDesignApi.extract(data);
      set({ lastExtraction: result });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Extraction failed' });
    } finally {
      set({ loading: false });
    }
  },

  submitAsyncExtraction: async (data) => {
    set({ loading: true, error: null });
    try {
      await brandDesignApi.extractAsync(data);
      set({ polling: true });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Async extraction failed' });
    } finally {
      set({ loading: false });
    }
  },

  pollStatus: async (clientUid) => {
    try {
      const status = await brandDesignApi.getStatus(clientUid);
      set({ pollingStatus: status });
      if (status.status === 'completed' || status.status === 'failed') {
        set({ polling: false });
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to poll status' });
      set({ polling: false });
    }
  },

  fetchTokens: async (clientUid) => {
    set({ loading: true, error: null, tokens: null });
    try {
      const tokens = await brandDesignApi.getTokens(clientUid);
      set({ tokens });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load tokens' });
    } finally {
      set({ loading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
